"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { Locale } from "./config";
import type { Dictionary } from "./dictionaries";

type I18nValue = { locale: Locale; t: Dictionary };
type Store = I18nValue & { raw: Record<string, unknown> };

const I18nContext = createContext<Store | null>(null);

// Keys React or tooling may probe on any object; not dictionary lookups.
const IGNORED = new Set(["then", "toJSON", "constructor", "$$typeof", "@@iterator"]);

/** Development aid: report reads of namespaces the current area did not load. */
function guard(raw: Record<string, unknown>): Dictionary {
  if (process.env.NODE_ENV === "production") return raw as Dictionary;
  return new Proxy(raw, {
    get(target, key, receiver) {
      if (typeof key === "string" && !(key in target) && !IGNORED.has(key)) {
        console.error(
          `[i18n] "${key}" is not loaded here. Add its namespace to the nearest <I18nProvider messages>.`,
        );
      }
      return Reflect.get(target, key, receiver);
    },
  }) as Dictionary;
}

/**
 * Makes the request locale and the given messages available to client
 * components. Providers nest: an area layout adds its namespaces on top of
 * the ones loaded by the root layout.
 */
export function I18nProvider({
  locale,
  messages,
  children,
}: {
  locale: Locale;
  messages: object;
  children: ReactNode;
}) {
  const parent = useContext(I18nContext);
  const value = useMemo<Store>(() => {
    const inherited = parent && parent.locale === locale ? parent.raw : {};
    const raw = { ...inherited, ...(messages as Record<string, unknown>) };
    return { locale, raw, t: guard(raw) };
  }, [parent, locale, messages]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** `{ locale, t }` for client components. */
export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n() must be used inside <I18nProvider>.");
  return value;
}
